'use client'

import { useMemo } from 'react'

import { useTableState } from '../context/table-state-provider.tsx'
import type { SortingState } from '../tanstack-table'

/**
 * Converts SortingState to backend-compatible sort parameters
 * @param sorting - The sorting state from table
 * @returns Object with sort[key]=value format
 */
export const convertSortingToParams = (sorting: SortingState): Record<string, string> => {
  const params: Record<string, string> = {}

  sorting.forEach(({ desc, id }) => {
    params[`sort[${id}]`] = desc ? 'desc' : 'asc'
  })

  return params
}

/**
 * Hook to get backend-ready sort parameters from table state
 */
export const useTableSortParams = () => {
  const { sorting } = useTableState(state => ({
    sorting: state.sorting,
  }))

  return useMemo(() => {
    return convertSortingToParams(sorting)
  }, [sorting])
}

/**
 * Hook to build complete fetch parameters including sorting
 * Can be extended to include filters, pagination, etc.
 */
export const useTableFetchParams = () => {
  const { sorting } = useTableState(state => ({
    sorting: state.sorting,
  }))

  return useMemo(() => {
    const sortParams = convertSortingToParams(sorting)

    // Can be extended with other params like filters, pagination
    return {
      ...sortParams,
      // Add other params here as needed
    }
  }, [sorting])
}
